import {
  apiErrorCode,
  apiErrorStatus,
  LEGAL_ERRORS,
  PLAN_UPGRADE_REQUIRED,
  WHATSAPP_ERRORS,
} from './whatsapp-errors';
import type { WhatsappAbandonReason, WhatsappConnectionReason } from '../types/whatsapp.types';

/* ==========================================================================
   Textos da conexão do WhatsApp, por estado e motivo

   Lidos pelo dono e pela equipe: linguagem da loja, sem termo interno (WABA,
   token, webhook). Ficam como dado para a faixa, a seção e o assistente
   falarem a mesma coisa sobre o mesmo motivo.
   ========================================================================== */

/** Onde o dono resolve cartão, restrição e qualidade: o Gerenciador de Negócios da Meta. */
export const META_BUSINESS_URL = 'https://business.facebook.com/';

export const CUSTOMER_AGENT_SETTINGS_PATH = '/settings/customer-agent';
export const WHATSAPP_CONNECT_PATH = '/settings/customer-agent/connect';

export const ONLY_OWNER_TITLE = 'Só o dono conecta o WhatsApp';

export const ONLY_OWNER_TEXT =
  'Conectar e desconectar o WhatsApp da loja é com o dono do estabelecimento. Se você é o dono e o recurso ' +
  'acabou de chegar, saia e entre de novo no painel para atualizar as suas permissões.';

export const APP_REMINDER_TEXT =
  'Abra o app WhatsApp Business no celular principal pelo menos a cada 14 dias: sem isso, a Meta desconecta o número.';

export interface ReasonCopy {
  title: string;
  text: string;
  /** Link para fora do painel, quando a solução é na Meta. */
  external?: { label: string; href: string };
}

const META_LINK = { label: 'Abrir o gerenciador da Meta', href: META_BUSINESS_URL };

/** `action_required`: o motivo e o que fazer. */
export function actionRequiredCopy(reason: WhatsappConnectionReason): ReasonCopy {
  switch (reason) {
    case 'payment_method_missing':
      return {
        title: 'Falta um cartão na Meta',
        text:
          'A Meta cobra as mensagens do WhatsApp direto da loja e só envia com um cartão cadastrado na conta do ' +
          'WhatsApp da loja na Meta. Cadastre o cartão no gerenciador da Meta: o envio volta sozinho.',
        external: META_LINK,
      };
    case 'meta_restricted':
      return {
        title: 'Número restrito pela Meta',
        text:
          'A Meta restringiu o número da loja, e o envio de mensagens está pausado. Veja o motivo e o prazo no ' +
          'gerenciador da Meta; quando a Meta liberar o número, a conexão volta sozinha.',
        external: META_LINK,
      };
    case 'quality_low':
      return {
        title: 'Qualidade baixa na Meta',
        text:
          'Clientes estão bloqueando ou denunciando as mensagens do número da loja, e a Meta baixou a qualidade ' +
          'dele. Confira no gerenciador da Meta: com a qualidade baixa, a Meta pode limitar o envio.',
        external: META_LINK,
      };
    case 'other_automation_detected':
      return {
        title: 'Outra automação no número',
        text:
          'Outra automação, uma resposta automática do app ou um WhatsApp Web automatizado está respondendo os ' +
          'clientes no mesmo número. Desligue a outra automação: com duas respondendo, as conversas se ' +
          'atropelam. A conexão volta ao normal 24 horas depois da última resposta automática.',
      };
    case 'app_inactive_warning':
      return {
        title: 'Abra o app no celular principal',
        text:
          'A Meta avisou que vai desconectar o número por falta de uso do app WhatsApp Business no celular ' +
          'principal. Abra o app no celular para manter a conexão.',
      };
    default:
      return {
        title: 'A conexão precisa de atenção',
        text: 'A Meta pediu uma ação na conta do WhatsApp da loja. Confira no gerenciador da Meta.',
        external: META_LINK,
      };
  }
}

/** `disconnected`: por que caiu. */
export function disconnectedCopy(reason: WhatsappConnectionReason): ReasonCopy {
  switch (reason) {
    case 'by_owner':
      return {
        title: 'WhatsApp desconectado',
        text:
          'O WhatsApp da loja foi desconectado pelo painel. O número continua funcionando normalmente no app ' +
          'do celular.',
      };
    case 'app_inactive':
      return {
        title: 'Desconectado pela Meta por falta de uso',
        text:
          'O app WhatsApp Business ficou 14 dias sem ser aberto no celular principal, e a Meta desconectou o ' +
          'número. Abra o app no celular e reconecte.',
      };
    case 'removed_in_app':
      return {
        title: 'Desconectado pelo app do celular',
        text: 'O número foi desconectado do Vapt Menu pelo app WhatsApp Business do celular. Se foi sem querer, reconecte.',
      };
    case 'token_revoked':
      return {
        title: 'Permissão da Meta retirada',
        text:
          'A permissão que a loja deu à Vapt Menu na Meta foi retirada ou venceu. Reconecte para dar a ' +
          'permissão de novo.',
      };
    case 'plan_entitlement_lost':
    case 'by_platform':
      return {
        title: 'Desconectado pelo fim do plano',
        text:
          'O WhatsApp ficou desconectado depois de 30 dias sem o plano do Atendente IA, e os dados das conversas ' +
          'guardados pelo canal foram apagados. Conecte de novo para o atendente voltar.',
      };
    default:
      return { title: 'WhatsApp desconectado', text: 'O WhatsApp da loja foi desconectado. Reconecte para o atendente voltar.' };
  }
}

/** Motivo de uma tentativa que não terminou (abandono ou recusa), no passado. */
export function failedCopy(reason: WhatsappConnectionReason): ReasonCopy {
  const title = 'A última tentativa não terminou';
  switch (reason) {
    case 'phone_number_in_use':
      return {
        title,
        text:
          'O número escolhido já está conectado a outra loja no Vapt Menu. Desconecte-o na outra loja ou fale ' +
          'com o suporte do Vapt Menu.',
      };
    case 'cancelled':
    case 'popup_blocked':
    case 'sdk_blocked':
    case 'app_version_too_old':
    case 'number_not_eligible':
    case 'meta_error':
      return { title, text: abandonMessage(reason) };
    default:
      return { title, text: 'A conexão não foi concluída. Tente de novo.' };
  }
}

/** O que dizer quando o pop-up termina sem conectar. */
export function abandonMessage(reason: WhatsappAbandonReason): string {
  switch (reason) {
    case 'cancelled':
      return 'A conexão foi cancelada antes de terminar. Quando quiser, tente de novo.';
    case 'popup_blocked':
      return 'O navegador bloqueou a janela de conexão. Permita pop-ups para o painel e tente de novo.';
    case 'sdk_blocked':
      return (
        'Um bloqueador de anúncios ou de rastreamento impediu a conexão com a Meta. Desative o bloqueador ' +
        'para o painel e tente de novo.'
      );
    case 'app_version_too_old':
      return (
        'O app WhatsApp Business do celular está desatualizado. Atualize o app pela loja de aplicativos do ' +
        'celular e tente de novo.'
      );
    case 'number_not_eligible':
      return (
        'A Meta não liberou este número para a conexão. Confira se é o número do WhatsApp Business que os ' +
        'clientes usam.'
      );
    case 'meta_error':
      return 'A Meta não concluiu a conexão agora. Tente de novo em alguns minutos.';
  }
}

/** A recusa da criação ou da conclusão da sessão, em linguagem da loja. */
export function signupErrorMessage(error: unknown): string {
  switch (apiErrorCode(error)) {
    case WHATSAPP_ERRORS.phoneNumberInUse:
      return failedCopy('phone_number_in_use').text;
    case WHATSAPP_ERRORS.numberNotEligible:
      return abandonMessage('number_not_eligible');
    case WHATSAPP_ERRORS.metaError:
      return abandonMessage('meta_error');
    case WHATSAPP_ERRORS.signupSessionExpired:
      return 'A tentativa passou de 15 minutos sem terminar. Comece de novo.';
    case WHATSAPP_ERRORS.signupSessionUsed:
      return 'Esta tentativa de conexão já foi usada. Comece de novo.';
    case WHATSAPP_ERRORS.alreadyConnected:
      return 'A loja já tem um número de WhatsApp conectado.';
    case PLAN_UPGRADE_REQUIRED:
      return 'O plano da loja não inclui o Atendente IA.';
    case LEGAL_ERRORS.acceptanceRequired:
      return 'Aceite os Termos de Uso e o DPA antes de conectar.';
    default:
      break;
  }
  const status = apiErrorStatus(error);
  if (status === 404) return 'Esta tentativa de conexão não existe mais. Comece de novo.';
  if (status === 403) return ONLY_OWNER_TEXT;
  return 'Não foi possível concluir a conexão agora. Tente de novo.';
}
